import { useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Play, Pause, Music2, Loader2 } from "lucide-react";
import { getAlbumPreviews } from "@/lib/previews.functions";

function fmt(ms: number): string {
  if (!ms) return "";
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export function TrackPreviewList({
  artist,
  title,
}: {
  artist: string;
  title: string;
}) {
  const fn = useServerFn(getAlbumPreviews);
  const { data, isLoading } = useQuery({
    queryKey: ["previews", artist, title],
    queryFn: () => fn({ data: { artist, title } }),
    staleTime: 1000 * 60 * 30,
    enabled: !!artist && !!title,
  });

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const toggle = (url: string) => {
    if (playing === url) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    audioRef.current?.pause();
    const a = new Audio(url);
    a.volume = 0.9;
    a.addEventListener("ended", () => setPlaying(null));
    a.play().catch(() => setPlaying(null));
    audioRef.current = a;
    setPlaying(url);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-border bg-surface/60 p-4 text-xs text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Buscando prévias…
      </div>
    );
  }
  if (!data || data.length === 0) return null;

  return (
    <div className="rounded-xl border border-border bg-surface/60 p-4">
      <div className="mb-3 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
        <Music2 className="h-3.5 w-3.5" /> Prévias (30s)
      </div>
      <ul className="divide-y divide-border/60">
        {data.map((t) => {
          const isThis = playing === t.previewUrl;
          return (
            <li
              key={t.previewUrl}
              className="flex items-center gap-3 py-2 text-sm"
            >
              <button
                type="button"
                onClick={() => toggle(t.previewUrl)}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-transform hover:scale-105"
                aria-label={isThis ? "Pausar" : "Tocar"}
              >
                {isThis ? (
                  <Pause className="h-3.5 w-3.5" />
                ) : (
                  <Play className="h-3.5 w-3.5 translate-x-[1px]" />
                )}
              </button>
              <span className="w-5 text-right text-[10px] tabular-nums text-muted-foreground">
                {t.trackNumber || "—"}
              </span>
              <span className="flex-1 truncate">{t.title}</span>
              <span className="text-[10px] tabular-nums text-muted-foreground">
                {fmt(t.durationMs)}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
